import TableScrollShell from "../common/TableScrollShell";
import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Search,
  X,
  Pencil,
  Trash2,
  Move,
  CircleAlert,
  CircleCheck,
  Plus,
  Inbox,
  SearchX,
} from "lucide-react";
import {
  fetchCompletionRules,
  createCompletionRule,
  deleteCompletionRule,
  updateRule,
} from "../../service/Configuration";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import PageSizeSelector from "../common/PageSizeSelector";
import DeletePermission from "../suspence/DeletePermission";
import { getActivityListByProjectId } from "../../service/User";
import { getPermittedActivities } from "../../utils/activityPermissions";
import { getPermissionByScreenCode } from "../../utils/permissions";
import { Spinner } from "../../Spinner";
import PagerArrowButton from "../common/PagerArrowButton";
import ActionIconButton from "../common/ActionIconButton";
import CompletionRuleModal from "./CompletionRuleModal";
import SearchFieldPicker from "../common/SearchFieldPicker";
import OverflowTooltipText from "../common/OverflowTooltipText";

// Same field-scoped search pattern as AssignmentTable.js (Special Assignment
// Rule) and UserToolbar.js (User Management): a joined field-picker + search
// box via the shared SearchFieldPicker component, instead of one box
// silently matching every field at once. Default scope matches this table's
// first data column (Status), same rule AssignmentTable.js follows. Only
// covers the two fields the old single search box already matched (status,
// rule expression) -- Active/Deactive and Move Task were never searchable
// before and stay that way here, to keep this change scoped to a style
// change rather than adding new search coverage.
const SEARCH_SCOPES = [
  { value: "status", labelKey: "status", fallback: "Status" },
  { value: "rule", labelKey: "ruleLabel", fallback: "Rule" },
];

const STATUS_META = {
  SUBMISSION: { dot: "bg-blue-500", text: "text-blue-700", bg: "bg-blue-50" },
  NOT_COMPLETED: { dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50" },
};

// search/searchScope are lifted to ConfigurationMain.js (which stays mounted
// while switching tabs) so the typed term and selected field survive a tab
// switch instead of resetting -- see AssignmentTable.js for the same change.
export const CompletionRules = ({ tabBar, search, onSearchChange: setSearch, searchScope, onSearchScopeChange: setSearchScope }) => {
  const { langMode, theme, projectId, activityId, updateActivityId } = useContext(AppContext);
  const isRtl = langMode === "ar" || langMode === "ur";

  // Create-form fields (Add new rule modal)
  const [criteria, setCriteria] = useState("");
  const [selectedIcon, setSelectedIcon] = useState(
    <CircleCheck className="text-green-500" />
  );
  const [status, setStatus] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [iconStatus, setIconstatus] = useState("");
  const [moveTasks, setMoveTask] = useState(false);
  const [selectedActivity2, setSelectedActivity2] = useState(0);
  const [isAddOpen, setIsAddOpen] = useState(false);

  // Edit modal -- a local draft copied from the clicked row on open, only
  // committed via handleUpdateChanges (the same call the old inline "Update"
  // button made) when Save is clicked.
  const [editingRule, setEditingRule] = useState(null); // { id, index }
  const [editDraft, setEditDraft] = useState(null);

  const [completionRules, setCompletionRules] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [deleteRule, setDeleteRule] = useState(null);
  const [activity2, setActivity2] = useState([]);

  const customVarPerm = getPermissionByScreenCode("CONFIGURATION");

  const getBackendMessage = (payload) =>
    payload?.body?.value ??
    payload?.body?.error ??
    payload?.header?.message ??
    payload?.message;

  const totalItems = Number.isFinite(Number(totalCount)) ? Number(totalCount) : 0;
  const totalPages = Math.max(0, Math.ceil(totalItems / size) - 1);
  const startIndex = page * size;
  const endIndex = startIndex + size;
  const displayStart = totalItems > 0 ? startIndex + 1 : 0;
  const displayEnd = totalItems > 0 ? Math.min(endIndex, totalItems) : 0;

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setPage(0);
  };

  const goToPreviousPage = () => {
    if (page > 0) setPage(page - 1);
  };

  const goToNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const statusoptions = [
    { id: "SUBMISSION", value: "SUBMISSION" },
    { id: "NOT_COMPLETED", value: "NOT_COMPLETED" },
  ];
  const mappedStatusOptions = statusoptions.map((option) => ({
    value: option.id,
    label: option.value,
  }));

  const handleToggle = () => setIsActive(!isActive);

  const loadCompletionRules = async (reset = false) => {
    setIsLoading(true);
    setError(null);
    try {
      await fetchCompletionRules(activityId, page, size, setCompletionRules, setTotalCount);
    } catch (error) {
      console.error("Error loading completion rules:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activityId) {
      loadCompletionRules(true);
    }
  }, [activityId, page, size]);

  const openAddModal = () => {
    if (!activityId) {
      toast.error(translations[langMode].PleaseSelectWorkflowBeforeAddingRule);
      return;
    }
    setIsAddOpen(true);
  };
  const closeAddModal = () => setIsAddOpen(false);

  const handleSaveChanges = async () => {
    if (!activityId || !criteria || !status) {
      toast.error(translations[langMode].pleaseFillAllFieldsBeforeSaving);
      return;
    }
    const moveTask = status?.value === "NOT_COMPLETED" ? moveTasks : false;
    const targetWorkflowId = moveTask && selectedActivity2 ? selectedActivity2.value : null;

    try {
      const responseData = await createCompletionRule(
        activityId,
        criteria,
        iconStatus,
        isActive,
        moveTask,
        targetWorkflowId
      );
      if (responseData.header.code === 600) {
        setStatus("");
        setCriteria("");
        toast.success(translations[langMode].ruleCreatedSuccessfully);
        loadCompletionRules(true); // Reload the rules after creating a new one
        setSelectedActivity2([]);
        setMoveTask(false);
        setIsAddOpen(false);
      } else {
        const backendMessage = getBackendMessage(responseData);
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (error) {
      console.error("Error creating completion rule:", error);
      const backendMessage =
        error?.response?.data?.body?.value ??
        error?.response?.data?.body?.error ??
        error?.response?.data?.header?.message ??
        error?.response?.data?.message ??
        error?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      }
    }
  };

  const handleDeleteRule = async () => {
    try {
      const response = await deleteCompletionRule(deleteRule);
      if (response.header.code === 600) {
        toast.success(translations[langMode].ruledeleted);
        setCompletionRules((prevRules) =>
          prevRules.filter((rule) => rule.id !== deleteRule)
        );
        setDeleteRule(null);
        loadCompletionRules();
        setPage(0);
        setSize(10);
      } else {
        const backendMessage = getBackendMessage(response);
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (error) {
      console.error("Error deleting completion rule:", error);
      const backendMessage =
        error?.response?.data?.body?.value ??
        error?.response?.data?.body?.error ??
        error?.response?.data?.header?.message ??
        error?.response?.data?.message ??
        error?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      }
    }
  };

  const handleStatus = (selectedStatus) => {
    setStatus(selectedStatus);
    if (selectedStatus?.value !== "NOT_COMPLETED") {
      setMoveTask(false);
      setSelectedActivity2([]);
    }
    if (selectedStatus.value === "SUBMISSION") {
      setSelectedIcon(<CircleCheck className="text-green-500" />);
      setIconstatus("SUBMISSION");
    } else if (selectedStatus.value === "NOT_COMPLETED") {
      setSelectedIcon(<CircleAlert className="text-yellow-400" />);
      setIconstatus("NOT_COMPLETED");
    }
  };

  const handleUpdateChanges = async (
    ruleid,
    iconStatus,
    criteria,
    activityId,
    isActive,
    moveTask,
    targetWorkflowId
  ) => {
    try {
      const finalMoveTask = iconStatus === "NOT_COMPLETED" && Boolean(moveTask);
      const finalTargetWorkflowId = finalMoveTask ? targetWorkflowId : null;
      const response = await updateRule(
        ruleid,
        iconStatus,
        criteria,
        activityId,
        isActive,
        finalMoveTask,
        finalTargetWorkflowId
      );
      if (response.header.code === 600) {
        toast.success(translations[langMode].ruleupdated);
        loadCompletionRules(true);
        setSelectedActivity2([]);
        setMoveTask(false);
        setEditingRule(null);
        setEditDraft(null);
      } else {
        const backendMessage = getBackendMessage(response);
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (error) {
      console.error("Error updating completion rule:", error);
      const backendMessage =
        error?.response?.data?.body?.value ??
        error?.response?.data?.body?.error ??
        error?.response?.data?.header?.message ??
        error?.response?.data?.message ??
        error?.message;
      if (backendMessage) {
        toast.error(backendMessage);
      }
    }
  };

  useEffect(() => {
    const fetchActivityList = async () => {
      try {
        const data = await getActivityListByProjectId(projectId);
        if (Array.isArray(data)) {
          setActivity2(data);
        } else {
          console.warn("getActivityListByProjectId did not return an array:", data);
          setActivity2([]);
        }
      } catch (error) {
        console.error("Error fetching activities:", error);
        setActivity2([]);
      }
    };

    fetchActivityList();
  }, [projectId]);

  const handleChangeActivity = async (selectedValue) => {
    setSelectedActivity2(selectedValue);
    localStorage.setItem("workflowName2", selectedValue.label);
  };

  const permittedActivity2 = useMemo(() => {
    return getPermittedActivities(activity2);
  }, [activity2]);

  const option = useMemo(() => {
    return (permittedActivity2 || [])
      .filter(Boolean)
      .map((item) => ({
        value: item.workflowId,
        label: item.workflowName,
      }));
  }, [permittedActivity2]);

  // Edit modal -- operates on a local draft of the clicked row; same fields
  // the old per-row inline handlers mutated, just scoped to one draft object
  // instead of completionRules[index], and committed via handleUpdateChanges
  // (unchanged) on Save.
  const openEditModal = (rule, index) => {
    setEditingRule({ id: rule.id, index });
    setEditDraft({
      status: rule.status,
      icon: rule.icon,
      activeStatus: rule.activeStatus,
      moveTask: rule.moveTask,
      targetWorkflowId: rule.targetWorkflowId,
      rule: rule.rule,
    });
  };
  const closeEditModal = () => {
    setEditingRule(null);
    setEditDraft(null);
  };
  const handleEditStatusChange = (selectedOption) => {
    if (!selectedOption) return;
    setEditDraft((d) => ({
      ...d,
      status: selectedOption.value,
      icon: selectedOption.value,
      // Task movement only exists for NOT_COMPLETED rules.
      ...(selectedOption.value !== "NOT_COMPLETED" ? { moveTask: false, targetWorkflowId: null } : {}),
    }));
  };
  const handleEditActiveToggle = (nextActive) => {
    setEditDraft((d) => ({ ...d, activeStatus: nextActive }));
  };
  const handleEditMoveTaskToggle = (nextMoveTask) => {
    setEditDraft((d) => ({ ...d, moveTask: nextMoveTask }));
  };
  const handleEditTargetWorkflowChange = (selectedOption) => {
    setEditDraft((d) => ({
      ...d,
      targetWorkflowId: selectedOption ? selectedOption.value : null,
    }));
  };
  const handleEditCriteriaChange = (e) => {
    setEditDraft((d) => ({ ...d, rule: e.target.value }));
  };
  const submitEdit = () => {
    if (!editingRule || !editDraft) return;
    handleUpdateChanges(
      editingRule.id,
      editDraft.icon,
      editDraft.rule,
      activityId,
      editDraft.activeStatus,
      editDraft.moveTask,
      editDraft.targetWorkflowId
    );
  };

  // Client-side filter over the currently loaded page -- no new API params,
  // pagination/fetch logic above is untouched.
  //
  // Field-scoped search -- same architecture as AssignmentTable.js. `status`
  // is compared with startsWith, not includes -- "NOT_COMPLETED" contains
  // "COMPLETED" as a literal substring (NOT_-COMPLETED-), so an includes()
  // check made typing "completed" also match Not Completed rows. startsWith
  // keeps "completed" matching only Completed while "not"/"not_completed"
  // still matches only Not Completed.
  const displayedRules = useMemo(() => {
    if (!search.trim()) return completionRules;
    const q = search.trim().toLowerCase();

    const matchesStatus = (r) => (r.status || "").toLowerCase().startsWith(q);
    const matchesRule = (r) => (r.rule || "").toLowerCase().includes(q);

    return completionRules.filter((r) => (searchScope === "rule" ? matchesRule(r) : matchesStatus(r)));
  }, [completionRules, search, searchScope]);

  const scopeOptions = SEARCH_SCOPES.map((s) => ({
    value: s.value,
    label: translations[langMode][s.labelKey] || s.fallback,
  }));
  const currentScopeLabel = scopeOptions.find((s) => s.value === searchScope)?.label;
  // Changing the search field makes the previously typed term ambiguous (it
  // was typed against a different field) -- same reset UserToolbar.js does
  // in its own handleScopeSelect.
  const handleScopeSelect = (value) => {
    setSearchScope(value);
    setSearch("");
  };
  const currentSearchPlaceholder =
    searchScope === "status"
      ? translations[langMode].searchStatusOrExpression
      : `${translations[langMode]?.search || "Search"} ${currentScopeLabel || ""}…`;

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="shrink-0 flex flex-col gap-3 mb-3 rounded-xl border border-slate-200 bg-white shadow-sm">
        {tabBar && (
          <div className="flex w-full min-w-0 items-center justify-between gap-2 px-3 pt-3 sm:px-5 sm:pt-4">
            {tabBar}
          </div>
        )}
        <div className="flex flex-col gap-3 px-3 py-3 sm:px-5 sm:py-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2
              className={`text-base font-bold text-slate-800 leading-tight sm:text-lg ${theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                }`}
            >
              {translations[langMode].complitionrule}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {translations[langMode].completionRuleSubheading}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Joined button+input pill -- same visual/interaction pattern as
                AssignmentTable.js (Special Assignment Rule) and UserToolbar.js
                (User Management) via the shared SearchFieldPicker component,
                so this table's search looks and behaves like the rest of the
                app's field-scoped list searches. */}
            <div className="flex items-center w-full sm:w-72">
              <SearchFieldPicker
                options={scopeOptions}
                activeValue={searchScope}
                onSelect={handleScopeSelect}
                searchPlaceholder={translations[langMode]?.findAField ?? "Find a field..."}
                isRtl={isRtl}
                buttonTestId="completion-rules-search-scope-button"
                buttonClassName="!h-10"
              />
              <div className="relative flex-1 flex items-center">
                <Search size={14} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 shrink-0 ${isRtl ? "right-3" : "left-3"}`} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={typeof window !== "undefined" && window.innerWidth < 450 ? `${translations[langMode]?.search || "Search"}...` : currentSearchPlaceholder}
                  title={currentSearchPlaceholder}
                  aria-label={currentSearchPlaceholder}
                  className={`w-full h-10 text-sm outline-none bg-white border border-slate-200 focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] placeholder:text-slate-400 ${isRtl ? "pr-9 pl-8 border-r-0 rounded-l-lg" : "pl-9 pr-8 border-l-0 rounded-r-lg"}`}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className={`absolute top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 shrink-0 ${isRtl ? "left-2" : "right-2"}`}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <CommonButton colorIntent="primary"
              onClick={openAddModal}
              variant="primary"
              tooltipText={translations[langMode].addcompletionrule}
              tooltipPosition="top"
              disabled={!customVarPerm?.canCreate}
              wrapperClassName="w-full sm:w-auto"
              className="w-full sm:w-auto !h-10 !min-h-[40px] !rounded-[10px] !px-3 !py-2 !text-xs sm:!px-4 sm:!text-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              <Plus size={18} /> {translations[langMode].addNewRule}
            </CommonButton>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="relative z-20 flex flex-wrap items-center justify-between gap-2 sm:gap-3 border-b border-slate-100 bg-white px-3 py-2.5 sm:px-5">
          <p className="flex items-center gap-2 text-xs text-slate-500 sm:gap-4">
            <span className="text-xs text-slate-500 whitespace-nowrap sm:hidden">
              <span className="rtl-keep-ltr">{`${displayStart}-${displayEnd}`}</span>
              {` / `}
              <span className="rtl-keep-ltr">{totalItems ?? 0}</span>
            </span>
            <span className="hidden sm:inline whitespace-nowrap">
              {`${translations[langMode].showing || "Showing"} `}
              <span className="rtl-keep-ltr">{`${displayStart}-${displayEnd}`}</span>
              {` ${translations[langMode].of || "of"} `}
              <span className="rtl-keep-ltr">{totalItems ?? 0}</span>
            </span>
          </p>
          <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:gap-3">
            <div className="flex items-center gap-1.5">
              <label htmlFor="pageSize" className="hidden sm:inline whitespace-nowrap">
                {translations[langMode].rows}
              </label>
              <PageSizeSelector value={size} onChange={handlePageSizeChange} height="32px" fontSize="12px" />
            </div>
            <span className="pager-nav flex items-center gap-1">
              <PagerArrowButton
                direction="prev"
                isRtl={isRtl}
                onClick={goToPreviousPage}
                disabled={page === 0}
                ariaLabel={translations[langMode].previousPage}
              />
              <span className="text-xs text-slate-600 px-1 whitespace-nowrap">
                {page + 1} / {Math.max(totalPages + 1, 1)}
              </span>
              <PagerArrowButton
                direction="next"
                isRtl={isRtl}
                onClick={goToNextPage}
                disabled={page === totalPages}
                ariaLabel={translations[langMode].nextPage}
              />
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="py-10">
            <Spinner />
          </div>
        ) : (
          <TableScrollShell theme={theme} className="w-full flex-1 min-h-0" innerClassName="task-list-scrollbar">
            <table className="w-full text-xs border-separate border-spacing-0">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-center border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-400 w-14">
                    {translations[langMode].number}
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                    {translations[langMode].status}
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                    {translations[langMode].activedeactive}
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                    Move Task
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-left border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                    {translations[langMode].completionRuleColumn}
                  </th>
                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-right border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500">
                    {translations[langMode].Action}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedRules.map((rule, index) => {
                  const meta = STATUS_META[rule.status] || STATUS_META.NOT_COMPLETED;
                  const destinationLabel = option.find(
                    (opt) => opt.value === rule.targetWorkflowId
                  )?.label;
                  return (
                    <tr
                      key={rule.id ?? index}
                      className="hover:bg-slate-50/70 transition-colors"
                    >
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100 text-center">
                        <span className="font-mono text-xs text-[#70788f] whitespace-nowrap">
                          {page * size + index + 1}
                        </span>
                      </td>
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100 whitespace-nowrap">
                        <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-md ${meta.bg} ${meta.text}`}>
                          {rule.status}
                        </span>
                      </td>
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100 whitespace-nowrap">
                        <span
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md"
                          style={
                            rule.activeStatus
                              ? { color: "#188A4B", background: "#E7F6ED" }
                              : { color: "#C22B3B", background: "#FBE9EB" }
                          }
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ background: rule.activeStatus ? "#188A4B" : "#C22B3B" }}
                          />
                          {rule.activeStatus
                            ? translations[langMode].active || "Active"
                            : translations[langMode].inactive || "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100">
                        {rule.moveTask ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-violet-50 text-violet-700">
                            <Move size={11} /> {destinationLabel || "…"}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100 max-w-60">
                        {rule.rule ? (
                          <OverflowTooltipText text={rule.rule} className="font-mono text-xs text-slate-500" />
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-5 py-2.5 border-b border-slate-100">
                        <div className="flex items-center justify-end gap-1.5">
                          <ActionIconButton
                            tone="blue"
                            ariaLabel={translations[langMode].editRule}
                            tooltipText={translations[langMode].editRule}
                            tooltipPosition="top"
                            onClick={() => openEditModal(rule, index)}
                            className="!h-7 !w-7 !rounded-md"
                          >
                            <Pencil size={14} />
                          </ActionIconButton>
                          <ActionIconButton
                            tone="red"
                            disabled={!customVarPerm?.canDelete}
                            ariaLabel={translations[langMode].deleteRule}
                            tooltipText={translations[langMode].deleteRule}
                            tooltipPosition="top"
                            onClick={() => setDeleteRule(rule.id)}
                            className="!h-7 !w-7 !rounded-md"
                          >
                            <Trash2 size={14} />
                          </ActionIconButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {displayedRules.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-16 h-[200px] align-middle">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3 bg-[#E0EBF4] text-[#236DB4]">
                          {search.trim() ? <SearchX size={22} /> : <Inbox size={22} />}
                        </div>
                        <div className="text-sm font-semibold text-slate-800">
                          {search.trim() ? translations[langMode].noSearchResults : translations[langMode].norule}
                        </div>
                        <div className="text-xs mt-1 max-w-xs text-slate-400">
                          {search.trim() ? translations[langMode].tryAdjustingSearch : translations[langMode].getStartedAddingRule}
                        </div>
                        {search.trim() ? (
                          <button
                            type="button"
                            onClick={() => setSearch("")}
                            className="mt-3 h-8 px-3 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
                          >
                            {translations[langMode].clearSearch}
                          </button>
                        ) : (
                          customVarPerm?.canCreate && (
                            <button data-button-intent="primary"
                              type="button"
                              onClick={openAddModal}
                              className="mt-3 h-8 px-3 rounded-lg text-xs font-medium text-white bg-[#236DB4] hover:bg-[#1d5a96]"
                            >
                              {translations[langMode].addNewRule}
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableScrollShell>
        )}
      </div>

      <CompletionRuleModal
        open={isAddOpen}
        onClose={closeAddModal}
        isNew
        statusOptions={mappedStatusOptions}
        statusValue={status}
        onStatusChange={handleStatus}
        isActive={isActive}
        onToggleActive={handleToggle}
        moveTask={moveTasks}
        onToggleMoveTask={() => setMoveTask(!moveTasks)}
        workflowOptions={option}
        targetWorkflowValue={selectedActivity2}
        onTargetWorkflowChange={handleChangeActivity}
        criteria={criteria}
        onCriteriaChange={(e) => setCriteria(e.target.value)}
        onSave={handleSaveChanges}
        saveDisabled={!customVarPerm?.canCreate}
      />

      {editingRule && editDraft && (
        <CompletionRuleModal
          open={!!editingRule}
          onClose={closeEditModal}
          isNew={false}
          ruleNumber={editingRule.index + 1}
          statusOptions={mappedStatusOptions}
          statusValue={mappedStatusOptions.find((o) => o.value === editDraft.status) || null}
          onStatusChange={handleEditStatusChange}
          isActive={editDraft.activeStatus}
          onToggleActive={handleEditActiveToggle}
          moveTask={editDraft.moveTask}
          onToggleMoveTask={handleEditMoveTaskToggle}
          workflowOptions={option}
          targetWorkflowValue={option.find((opt) => opt.value === editDraft.targetWorkflowId) || null}
          onTargetWorkflowChange={handleEditTargetWorkflowChange}
          criteria={editDraft.rule}
          onCriteriaChange={handleEditCriteriaChange}
          onSave={submitEdit}
          saveDisabled={!customVarPerm?.canUpdate}
        />
      )}

      {deleteRule !== null && (
        <DeletePermission
          deleteFunction={handleDeleteRule}
          onClose={() => setDeleteRule(null)}
          onDeleteText={translations[langMode].aresurewantodeleterule}
        />
      )}
    </div>
  );
};
